
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  BarChart, 
  Users, 
  FileText, 
  Settings, 
  Mail, 
  CheckCircle, 
  ShieldAlert, 
  DollarSign, 
  Home, 
  Utensils,
  Menu,
  X
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { usePlatform } from '@/contexts/PlatformContext';
import { cn } from '@/lib/utils';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Drawer, DrawerContent, DrawerTrigger } from '@/components/ui/drawer';
import { useIsMobile } from '@/hooks/use-mobile';

export const AdminNav = () => {
  const location = useLocation();
  const { isDesktop } = usePlatform();
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);

  // Add body class for admin routes on mobile to enable proper scrolling
  useEffect(() => {
    if (!isDesktop && location.pathname.startsWith('/admin')) {
      document.body.classList.add('admin-route');
      document.documentElement.classList.add('admin-route');
    }
    
    return () => {
      document.body.classList.remove('admin-route');
      document.documentElement.classList.remove('admin-route');
    };
  }, [isDesktop, location.pathname]);

  const isActive = (path: string) => {
    return location.pathname === path;
  };
  
  const navItems = [
    { name: 'Dashboard', path: '/admin/dashboard', icon: Home },
    { name: 'Users', path: '/admin/users', icon: Users },
    { name: 'Meals', path: '/admin/meals', icon: Utensils },
    { name: 'Transactions', path: '/admin/transactions', icon: DollarSign },
    { name: 'Payouts', path: '/admin/payouts', icon: BarChart },
    { name: 'Verifications', path: '/admin/verifications', icon: CheckCircle },
    { name: 'Disputes', path: '/admin/disputes', icon: ShieldAlert },
    { name: 'Reporting', path: '/admin/reporting', icon: FileText },
    { name: 'Email Templates', path: '/admin/email-templates', icon: Mail },
    { name: 'Settings', path: '/admin/settings', icon: Settings },
  ];

  const renderNavItems = () => (
    <ul className="space-y-2">
      {navItems.map((item) => (
        <li key={item.path}>
          <Button
            variant={isActive(item.path) ? 'default' : 'ghost'}
            asChild
            className="w-full justify-start"
            onClick={() => setIsOpen(false)}
          >
            <Link to={item.path} className="flex items-center gap-3">
              <item.icon className="h-4 w-4" />
              {item.name}
            </Link>
          </Button>
        </li>
      ))}
    </ul>
  );
  
  // For desktop: show regular sidebar
  if (isDesktop) {
    return (
      <aside className="w-64 border-r border-border h-screen fixed">
        <div className="p-6">
          <h1 className="text-xl font-bold">Admin Panel</h1>
        </div>
        <nav className="px-4 pb-6">
          {renderNavItems()}
        </nav>
      </aside>
    );
  }
  
  // For mobile: show a drawer for iOS and sheet for others with higher z-index
  return (
    <div className="lg:hidden fixed top-4 left-4 z-[100]">
      {isMobile ? (
        <Drawer open={isOpen} onOpenChange={setIsOpen}>
          <DrawerTrigger asChild>
            <Button variant="outline" size="icon" className="rounded-full shadow-md">
              <Menu className="h-4 w-4" />
            </Button>
          </DrawerTrigger>
          <DrawerContent className="p-6 pt-10 z-[100]">
            <div className="max-h-[60vh] overflow-y-auto">
              <h2 className="text-xl font-bold mb-6">Admin Navigation</h2>
              <nav>{renderNavItems()}</nav>
            </div>
          </DrawerContent>
        </Drawer>
      ) : (
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" size="icon" className="rounded-full shadow-md">
              <Menu className="h-4 w-4" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[250px] z-[100]">
            <div className="py-6">
              <h2 className="text-xl font-bold mb-6">Admin Navigation</h2>
              <nav>{renderNavItems()}</nav>
            </div>
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
};
